<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import PanelEmptyState from 'dashboard/components-next/panel/PanelEmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  WORKBENCH_CONVERSATION_PANEL_CARD_CLASS,
  WORKBENCH_PANEL_CARD_END_CLASS,
  WORKBENCH_PANEL_CARD_START_CLASS,
} from 'dashboard/components/conversationColumnLayout';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    PanelEmptyState,
    NextButton,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
    // Conversa pedida pela rota. Serve só para reconhecer um link que o
    // servidor recusou (apagada, outra conta, caixa sem acesso).
    conversationId: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
      unavailableConversationId: 'getUnavailableConversationId',
    }),
    isUnavailableConversation() {
      return (
        !this.currentChat.id &&
        Boolean(this.conversationId) &&
        Number(this.conversationId) === Number(this.unavailableConversationId)
      );
    },
    filteredDashboardApps() {
      return this.dashboardApps.filter(app => app.show_in_navigation === false);
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.filteredDashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    // Desktop: mesmo cartão da lista. Sem margem à esquerda no workbench: o
    // handle de 8px já é o vão lista→chat. Na Caixa, não há handle, então
    // entra margem no começo. À direita, só quando este cartão é o último
    // (sem conversa, sem gutter do contato).
    conversationPanelClass() {
      if (this.isOnExpandedLayout) {
        return 'w-full bg-n-surface-1';
      }
      const extras = [];
      if (this.isInboxView) extras.push(WORKBENCH_PANEL_CARD_START_CLASS);
      if (!this.currentChat.id) extras.push(WORKBENCH_PANEL_CARD_END_CLASS);
      return [WORKBENCH_CONVERSATION_PANEL_CARD_CLASS, ...extras];
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
    backToQueue() {
      this.$router.push({ name: 'home' });
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col flex-1 min-h-0 min-w-0 relative"
    :class="conversationPanelClass"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      class="border-b border-b-n-weak"
    />
    <woot-tabs
      v-if="filteredDashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="h-10 shrink-0"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <!-- SiteUp: flex-1 min-h-0 (não h-full) — h-full no filho fazia o reply-box
         (rounded-2xl solid-1) escorregar por baixo do header. -->
    <div
      v-show="!activeIndex"
      class="flex flex-1 min-h-0 min-w-0 m-0 overflow-hidden"
    >
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <div
        v-if="isUnavailableConversation && !isInboxView"
        role="alert"
        data-testid="conversation-unavailable"
        class="flex flex-1 items-center justify-center p-6 bg-n-surface-1"
      >
        <PanelEmptyState
          class="max-w-sm"
          icon="i-ph-link-break"
          :title="$t('CONVERSATION.UNAVAILABLE.TITLE')"
          :hint="$t('CONVERSATION.UNAVAILABLE.HINT')"
        >
          <template #cta>
            <NextButton
              sm
              faded
              slate
              icon="i-ph-arrow-left"
              :label="$t('CONVERSATION.UNAVAILABLE.BACK_TO_QUEUE')"
              @click="backToQueue"
            />
          </template>
        </PanelEmptyState>
      </div>
      <EmptyState
        v-else-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in filteredDashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="filteredDashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
